/**
 * React Query mutation hook for deleting an expense
 * Handles expense deletion with automatic query invalidation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteExpense } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Hook to delete an expense using React Query mutation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback function called on successful deletion
 * @param {Function} options.onError - Callback function called on deletion error
 *
 * @returns {Object} Mutation object
 * @returns {Function} returns.mutate - Function to trigger the mutation
 * @returns {Function} returns.mutateAsync - Async function to trigger the mutation
 * @returns {boolean} returns.isLoading - Loading state
 * @returns {boolean} returns.isError - Error state
 * @returns {Object|null} returns.error - Error object if mutation failed
 *
 * @example
 * const deleteExpenseMutation = useDeleteExpenseQuery({
 *   onSuccess: () => {
 *     console.log('Expense deleted successfully');
 *   },
 *   onError: (error) => {
 *     console.error('Failed to delete expense:', error);
 *   },
 * });
 *
 * // Usage
 * deleteExpenseMutation.mutate({
 *   expense_sk: 'EXPENSE#123',
 *   is_lead: false,
 * });
 */
export const useDeleteExpenseQuery = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expense_sk, is_lead = false }) => {
      // Replace # with %23 for URL encoding
      const encodedSk = expense_sk ? expense_sk.replaceAll('#', '%23') : '';
      return deleteExpense(encodedSk, is_lead);
    },
    onSuccess: (data, variables, context) => {
      // Invalidate all expense queries to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });

      // Call custom onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Call custom onError callback if provided
      if (onError) {
        onError(error, variables, context);
      }
    },
  });
};

export default useDeleteExpenseQuery;
